import { Table } from "@/app/services/transactionService/getAvailableCredit";
import { cn } from "@/app/utils/cn";
import { formatDate, formatMonthAndYear } from "@/app/utils/formatDate";
import {
  addMoneySimbols,
  addMoneySimbolsCents,
} from "@/app/utils/formatMoneyValue";
import { GrayTableCard } from "@/view/components/GrayTableCard";
import { ReactNode } from "react";

interface ItemProps {
  title: string;
  value: string | ReactNode;
}

const Item = ({ title, value }: ItemProps) => {
  return (
    <div className="flex justify-between gap-2">
      <p className="text-sm font-medium text-black04">{title}</p>
      <span className="text-sm font-medium text-black01">{value}</span>
    </div>
  );
};

interface TableCardProps {
  table: Table;
  isSelected?: boolean;
  onClick?: () => void;
}

export function TableCard({
  table,
  isSelected = false,
  onClick,
}: TableCardProps) {
  if (!table.success) return <></>;

  return (
    <div
      className={cn(
        "flex max-w-fit cursor-pointer flex-col gap-4 rounded-lg border p-4 max-sm:min-w-[300px] max-sm:max-w-[300px] sm:max-w-max",
        isSelected && "border-primary shadow-md shadow-primary",
      )}
      onClick={() => onClick && onClick()}
    >
      <p>{table.tableName}</p>
      <div className="rounded-lg border border-primary bg-primary/10 p-2.5">
        <div className="flex justify-between gap-5 border-b pb-2.5 max-sm:flex-col max-sm:gap-3 max-sm:border-b-0">
          <div className="max-sm:flex max-sm:items-center max-sm:justify-between max-sm:border-b max-sm:border-gray-200 max-sm:pb-2">
            <p className="text-xs font-medium text-black04">Valor liquido</p>
            <span className="font-semibold max-sm:text-sm">
              {table.amount
                ? addMoneySimbolsCents(table.amount)
                : addMoneySimbols(table.ValorLiquidoCliente)}
            </span>
          </div>
          <div className="w-[1px] bg-border max-sm:hidden" />
          <div className="flex flex-col items-center max-sm:flex-col max-sm:items-start max-sm:justify-start max-sm:border-b max-sm:border-gray-200 max-sm:pb-2">
            <div className="flex flex-col items-center max-sm:w-full max-sm:flex-row max-sm:items-center max-sm:justify-between">
              <p className="mb-1 text-xs font-medium text-black04 max-sm:mb-0">
                Comissão ({table?.commission || 0}%
                {(table?.bonus || 0) > 0 && ` + ${table.bonus}%`})
              </p>
              <span className="mb-1 font-medium max-sm:mb-0 max-sm:ml-2 max-sm:text-sm">
                {table.amount
                  ? addMoneySimbolsCents(
                      ((table?.commission || 0) * table.amount) / 100,
                    )
                  : addMoneySimbols(
                      ((table?.commission || 0) * table.ValorLiquidoCliente) /
                        100,
                    )}
              </span>
            </div>
            {(table.bonus || 0) > 0 && (
              <div className="inline-flex items-center gap-1 rounded-[50px] bg-primary px-2 py-1 text-xs font-medium text-white max-sm:mt-2 max-sm:self-end">
                <span>Bônus</span>
                <span>
                  {table.amount
                    ? addMoneySimbolsCents(
                        ((table.bonus || 0) * table.amount) / 100,
                      )
                    : addMoneySimbols(
                        ((table.bonus || 0) * table.ValorLiquidoCliente) / 100,
                      )}
                </span>
              </div>
            )}
          </div>
          <div className="w-[1px] bg-border max-sm:hidden" />
          <div className="flex flex-col items-end max-sm:flex-row max-sm:items-center max-sm:justify-between">
            <p className="text-xs font-medium text-black04">Parcelas</p>
            <span className="max-sm:text-sm">{table.term || table.Prazo}</span>
          </div>
        </div>
        <div className="flex justify-between pt-2.5 max-sm:flex-col max-sm:gap-3 max-sm:pt-3">
          <div className="max-sm:flex max-sm:items-center max-sm:justify-between max-sm:border-b max-sm:border-gray-200 max-sm:pb-2">
            <p className="text-xs font-medium text-black04">
              Primeiro vencimento
            </p>
            <span className="text-sm font-medium">
              {formatMonthAndYear(
                table.firstDueDate || table.DataPrimeiroVencimento,
              )}
            </span>
          </div>
          <div className="flex flex-col items-end max-sm:flex-row max-sm:items-center max-sm:justify-between">
            <p className="text-xs font-medium text-black04">
              Ultimo vencimento
            </p>
            <span className="text-sm font-medium">
              {formatMonthAndYear(
                table.lastDueDate || table.DataUltimoVencimento,
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2.5 px-1">
        <Item
          title="Valor Solicitado"
          value={
            table.requestedAmount
              ? addMoneySimbolsCents(table.requestedAmount)
              : addMoneySimbols(table.ValorSolicitado)
          }
        />

        <Item
          title="Valor Principal"
          value={
            table.principalAmount
              ? addMoneySimbolsCents(table.principalAmount)
              : addMoneySimbols(table.ValorPrincipal)
          }
        />

        <Item
          title="Valor Bloqueado"
          value={
            table.blockedAmount
              ? addMoneySimbolsCents(table.blockedAmount)
              : addMoneySimbols(table.ValorBloqueado)
          }
        />

        <Item
          title="Taxa de Cadastro"
          value={
            table.processingFee
              ? addMoneySimbolsCents(table.processingFee)
              : addMoneySimbols(table.ValorTC)
          }
        />

        <Item
          title="IOF"
          value={
            table.iof
              ? addMoneySimbolsCents(table.iof)
              : addMoneySimbols(table.IOF)
          }
        />

        <Item
          title="Seguro"
          value={
            table.insuranceAmount
              ? addMoneySimbolsCents(table.insuranceAmount)
              : addMoneySimbols(table.ValorSeguro)
          }
        />
      </div>

      <GrayTableCard
        title="Parcelas"
        headers={["N°", "Venc", "Parcela", "Val. princ", "Juros"]}
        rows={(table.installments || table.Parcelas || []).map((p: any) => [
          p.n || p.NumeroParcela,
          formatDate(p.dueDate || p.DataVencimento),
          p.value
            ? addMoneySimbolsCents(p.value)
            : addMoneySimbols(p.ValorParcela),
          p.principalValue
            ? addMoneySimbolsCents(p.principalValue)
            : addMoneySimbols(p.ValorPrincipal),
          p.interestValue
            ? addMoneySimbolsCents(p.interestValue)
            : addMoneySimbols(p.ValorJuros),
        ])}
      />

      <GrayTableCard
        title="Taxas"
        headers={["Nome", "Mensal (AM)", "Anual (AA)"]}
        rows={(table.fees || table.Taxas || []).map((t: any) => [
          t.name,
          `${t.monthly ? (t.monthly / 100).toFixed(2) : (t.mensal || 0).toFixed(2)}%`,
          `${t.yearly ? (t.yearly / 100).toFixed(2) : (t.anual || 0).toFixed(2)}%`,
        ])}
      />
    </div>
  );
}
